import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, Plane, Ship, ChevronRight } from "lucide-react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";

export default function PhoneResults() {
  const [params] = useSearchParams();
  const phone = params.get("phone") || "";
  const navigate = useNavigate();
  const [shipments, setShipments] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
    setShipments(null);
    api
      .get("/track/by-phone", { params: { phone } })
      .then(({ data }) => setShipments(data))
      .catch((e) => {
        setShipments([]);
        setError(formatApiError(e.response?.data?.detail));
      });
  }, [phone]);

  return (
    <div className="min-h-[70vh] bg-[#F8FAFC] px-4 py-10 sm:px-8" data-testid="phone-results-page">
      <div className="mx-auto max-w-3xl">
        <Button variant="ghost" className="mb-6 gap-2 pl-0 text-sm text-slate-600"
                onClick={() => navigate("/")} data-testid="phone-back-button">
          <ArrowLeft className="h-4 w-4" /> Nouvelle recherche
        </Button>

        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
          Colis liés au numéro {phone}
        </h1>

        {shipments === null && (
          <p className="mt-6 text-slate-500" data-testid="phone-loading">Recherche en cours…</p>
        )}

        {error && (
          <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 p-6" data-testid="phone-error">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-6 w-6 shrink-0 text-[#DC2626]" />
              <p className="text-sm text-rose-800">{error}</p>
            </div>
          </div>
        )}

        <div className="mt-6 space-y-3">
          {shipments?.map((s) => {
            const Icon = s.shipping_type === "aerien" ? Plane : Ship;
            return (
              <button
                key={s.tracking_number}
                onClick={() => navigate(`/suivi/${s.tracking_number}`)}
                className="flex w-full flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-shadow hover:shadow-md"
                data-testid={`phone-result-${s.tracking_number}`}
              >
                <div>
                  <p className="font-mono text-lg font-extrabold tracking-wider text-[#1E3A8A]">
                    {s.tracking_number}
                  </p>
                  <p className="mt-1 flex items-center gap-2 text-sm text-slate-600">
                    <Icon className="h-4 w-4 text-[#2563EB]" />
                    {s.shipping_type === "aerien" ? "Aérien" : "Maritime"} · {s.weight_kg} kg ·{" "}
                    {s.destination_city} · livraison est. {formatDate(s.estimated_delivery)}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={s.status} />
                  <ChevronRight className="h-5 w-5 text-slate-400" />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
